







































import { Component, Prop } from 'vue-property-decorator';
import 'devextreme/data/odata/store';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { ContextsState } from '@/store';
import { Duration } from 'luxon';
import DataSource from 'devextreme/data/data_source';
import {
    Table,
    TableOptions,
    removeItemsFromView,
    getTableCaption,
} from '@/core/components/table';
import { Page } from '@/core/components/controls';
import { DxDataGrid } from 'devextreme-vue/data-grid';
import {
    CONTRACTS,
    WindowsServiceContract,
    FetchServiceContract,
    EventsServiceContract,
    Events
} from '@/core/contracts';
import { guid, TimeSpan } from '@/core/helpers';
import { DayInactivityInterval, ActivitySchedule } from '@/dto';
import { IContext } from '@/api/dto';
import DayInactivityIntervalsListTable from './DayInactivityIntervalsListTable.vue';
import { DayInactivityScheduleArgs } from './types';
import DevExpress from 'devextreme/bundles/dx.all';

@Component({
    components: {
        DxDataGrid,
        DayInactivityIntervalsListTable,
        TableOptions,
    },
})
export default class DayInactivityIntervalListPage extends Page {
    @Prop({ default: () => ({}) })
    public args!: DayInactivityScheduleArgs;
    public table: Table | null = null;
    public tableKey: string = 'dayInactivityInterval-control';
    public activitySchedules: ActivitySchedule[] | null = null;

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    private day: TimeSpan = new TimeSpan(Duration.fromObject({ days: 1 }));

    public setTable(table: Table): void {
        this.table = table;
    }

    public async mounted() {
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.refreshTable
        );
        this._eventsService!.on(
            Events.ProcessingStarted,
            this.refreshTable
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.refreshTable
        );
        this._eventsService!.off(
            Events.ProcessingStarted,
            this.refreshTable
        );
    }

    public get dataSource(): DataSource {
        return new DataSource({
            key: 'id' as keyof DayInactivityInterval,
            load: this.load,
            insert: (activityShedule: DayInactivityInterval) => this.insert(activityShedule),
            update: (id: string, data: DayInactivityInterval) => this.update(id, data),
        });
    }

    public get tableName(): string {
        return getTableCaption(this.tableKey);
    }

    public get isRowSelected() {
        return this.table && this.table!.getSelectedRowKeys().length === 1;
    }

    public addDayInactivityInterval() {
        this.table!.addRow();
    }

    public editDayInactivityInterval() {
        const selectedKey = this.table!.getSelectedRowKeys()[0];

        if (selectedKey) {
            const selectedIndex = this.table!.getRowIndexByKey(selectedKey);
            this.table!.editRow(selectedIndex);
        }
    }

    private get tableInstance(): DayInactivityIntervalsListTable {
        return this.$refs['table-component'] as DayInactivityIntervalsListTable;
    }

    private refreshTable({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.table!.refresh();
        }
    }

    private deleteDayInactivityInterval() {
        this.tableInstance.deleteDayInactivityInterval();
    }

    private get selectedAlias() {
        if (this.args && this.args.activityScheduleAlias && this.args.activityScheduleAlias !== '') {
            return this.args.activityScheduleAlias;
        } else {
            return null;
        }
    }

    private async load() {
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}/DayInactivityIntervals`;

        const activitySchedulesUrl = `/files/contexts/${currentContextId}/ActivitySchedules`;
        const activitySchedulesData = await this._fetchService!.get<any>(activitySchedulesUrl);
        this.activitySchedules = activitySchedulesData.activitySchedules;
        const data = await this._fetchService!.get<IContext>(url);
        const selectedAlias: string | null = this.selectedAlias;
        if (selectedAlias) {
            data.dayInactivityIntervals = data.dayInactivityIntervals!
                .filter(f => f.activityScheduleId === selectedAlias);
        }
        const result = data.dayInactivityIntervals ? this.transform(data.dayInactivityIntervals) : [];
        return result;
    }

    private transform(data: any[]) {
        return data.map((interval: any) => {
            const finishTimeOffset = TimeSpan.fromDto(interval.finishTimeOffset);
            if (finishTimeOffset.duration.days >= 1) {
                interval.finishTimeOffset = finishTimeOffset.duration.minus(this.day.duration).toFormat('hh:mm:ss');
            }
            return interval;
        });
    }

    private async insert(dayInactivityInterval: DayInactivityInterval) {
        if (this.selectedAlias) {
            dayInactivityInterval.activityScheduleId = this.selectedAlias;
        }
        dayInactivityInterval.id = guid();
        if (TimeSpan.compareStr(dayInactivityInterval.startTimeOffset, dayInactivityInterval.finishTimeOffset) >= 0) {
            dayInactivityInterval.finishTimeOffset = TimeSpan.fromDto(dayInactivityInterval.finishTimeOffset)
                                                     .duration.plus(this.day.duration).toFormat('dd.hh:mm:ss');
        }
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        const data = {
            DayInactivityIntervals_Partial: [dayInactivityInterval],
        };
        return await this._fetchService!.post<DayInactivityInterval>(
            url,
            JSON.stringify(data)
        );
    }

    private async update(id: string, dayInactivityInterval: DayInactivityInterval) {
        if (this.selectedAlias) {
            dayInactivityInterval.activityScheduleId = this.selectedAlias;
        }
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        dayInactivityInterval.id = id;
        if (dayInactivityInterval.finishTimeOffset &&
            dayInactivityInterval.startTimeOffset &&
            TimeSpan.compareStr(dayInactivityInterval.startTimeOffset, dayInactivityInterval.finishTimeOffset) >= 0) {
            dayInactivityInterval.finishTimeOffset = TimeSpan.fromDto(dayInactivityInterval.finishTimeOffset)
                                                     .duration.plus(this.day.duration).toFormat('dd.hh:mm:ss');
        }
        const data = {
            DayInactivityIntervals_Partial: [dayInactivityInterval],
        };
        return await this._fetchService!.post<DayInactivityInterval>(
            url,
            JSON.stringify(data)
        );
    }
}
